/*
 * Copyright © 2024 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {registerLocaleData} from '@angular/common';
import {inject} from '@angular/core';
import type {SupportedLocale} from '@dv/shared/code';
import {localeFrom, LogFactory} from '@dv/shared/code';
import {TranslocoService} from '@jsverse/transloco';
import moment from 'moment';
import type {Observable} from 'rxjs';
import {switchMap, take, tap} from 'rxjs';
import {map} from 'rxjs/operators';
import {reactOnUserLocale, UserLanguageService} from './user-language.service';

const LOG = LogFactory.createLog('i18n');

export function initMoment(): void {
    const momentLocales: Record<SupportedLocale, string> = {
        'de-CH': 'de',
        'fr-CH': 'fr',
    };

    const initialLocale = localeFrom(UserLanguageService.getLocalStorageLanguage());
    moment.locale(momentLocales[initialLocale] || 'de');

    reactOnUserLocale(locale$ => locale$.pipe(
        map(locale => momentLocales[locale]),
        map(momentLocale => moment.locale(momentLocale)),
        tap(momentLocale => LOG.trace('moment locale', momentLocale)),
    ));
}

export function initTransloco(): Observable<unknown> {
    const translateService = inject(TranslocoService);

   reactOnUserLocale(locale$ => locale$.pipe(
        tap(locale => LOG.trace('transloco set active language to', locale)),
        tap(locale => translateService.setActiveLang(locale)),
        tap(() => LOG.trace('transloco language changed')),
    ));

    // defer app initialization until translations are loaded - because we use translator.instant('...')
    return translateService.selectTranslate('COMMON.TODAY').pipe(
        take(1),
        tap(() => LOG.trace('transloco initialized')),
    );
}

export function initAngularLocales(): void {
    reactOnUserLocale(locale$ => locale$.pipe(
        switchMap(lazyAngularLocale),
    ));
}

async function lazyAngularLocale(locale: SupportedLocale): Promise<void> {
    switch (locale) {
        case 'de-CH': {
            const localeDe = await import('@angular/common/locales/de-CH');
            registerLocaleData(localeDe.default);
            break;
        }
        case 'fr-CH': {
            const localeFr = await import('@angular/common/locales/fr-CH');
            registerLocaleData(localeFr.default);
            break;
        }
    }
    LOG.trace('angular locale ', locale);
}
